import { IIconProps, IPersonaStyles } from '@fluentui/react';
import { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';

import AppContext from 'App/AppContext';
import { IPIconButton, IPScrollablePane, IPSpinner, IPText, IPTooltipHost } from 'baseComponents';
import { IPList } from 'baseComponents/List/IPList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { SkypeCircleCheckIcon } from 'components/Checklist/CheckListStyles';
import { useWidgetDrillDownNavigate } from 'components/Dashboard/WidgetDrillDownContext';
import SingleTask from 'components/Tasks/SingleTask';
import DateDividerListItem, {
  DataGroup,
  DateComparer,
  DateDivider,
  insertDividersAndGroupsInList,
} from 'components/Utils/ListItemDateDividers';
import { globalStackItemStylesPaddingSceneScroll } from 'globalStyles';
import Activity, { ActivityType } from 'models/activity';
import { EntityTypes } from 'models/entity';
import Task from 'models/tasks/task';
import { useTranslation } from 'react-i18next';
import { apiDismissActivity } from 'services/Api/dashboardService';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTimeMedium } from 'utils/datetime';
import { sortOnDate } from 'utils/sorting';
import { toNameInitial } from 'utils/string';
import { IWidgetRendererProps } from '../WidgetRenderer';

interface IWidgetRecentlyClosedTasksProps extends IWidgetRendererProps {}

const WidgetRecentlyClosedTasks: FunctionComponent<IWidgetRecentlyClosedTasksProps> = (
  props: IWidgetRecentlyClosedTasksProps,
) => {
  const { t } = useTranslation(['translation', 'dashboard', 'widgets']);
  const appContext = useContext(AppContext);
  const navigate = useWidgetDrillDownNavigate();
  const [recentClosedData, setRecentClosedData] = useState<Activity[] | undefined>(undefined);
  const [recentClosedDataDisplay, setRecentClosedDataDisplay] = useState<
    (Activity | DateDivider | DataGroup<Activity>)[] | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  const [showIcon, setShowIcon] = useState<number | undefined>(undefined);
  const dismissIcon: IIconProps = { iconName: 'RemoveFilter' };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget, props.sharedData]);

  useEffect(() => {
    if (recentClosedData && recentClosedData?.length > 0) {
      const newData = insertDividersAndGroupsInList(recentClosedData, getCompareDate, undefined, undefined);
      setRecentClosedDataDisplay(newData);
    } else {
      setRecentClosedDataDisplay(undefined);
    }
  }, [recentClosedData]);

  const loadData = async (refresh?: boolean) => {
    try {
      if (props.onRefreshSharedData && props.widget.dashboard && refresh) {
        await props.onRefreshSharedData();

        return;
      }

      if (isLoading) return;
      setIsLoading(true);

      const activities = props.sharedData?.activities;

      setRecentClosedData(
        activities
          ?.filter(
            (activity: Activity) =>
              activity.typeOfActivity === ActivityType.RecentlyClosed &&
              activity.entity.typeOfEntity === EntityTypes.Task,
          )
          .sort((a, b) => sortOnDate(b.created, a.created)),
      );
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getCompareDate: DateComparer<Activity> = (a) => {
    return a.created;
  };

  const getTaskDetail = async (taskId: number) => {
    if (!taskId) {
      return;
    }

    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasks = await apiGetSingleTask(taskId, false, accessToken, appContext.globalDataCache);
      if (tasks && tasks.tasks && tasks.tasks.length > 0) {
        const task = tasks.tasks[0];
        setSelectedTask(task);
      } else {
        appContext.showNotification(t('dashboard:Errors.ItemHasBeenDeleted'), true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const onPersonClickHandler = (activity: Activity) => {
    switch (activity.entity.typeOfEntity) {
      case EntityTypes.Requirement:
        navigate(`theme/${activity.entity.entityId}`);
        break;
      case EntityTypes.Control:
        navigate(`control/${activity.entity.entityId}`);
        break;
      case EntityTypes.Task:
        getTaskDetail(activity.entity.entityId);
        break;
      case EntityTypes.Risk:
        navigate(`/risk/${activity.entity.entityId}`);
        break;
      default:
        break;
    }
  };

  const dismissActivity = async (activityId: number) => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      await apiDismissActivity(activityId, accessToken);
      setRecentClosedData(recentClosedData?.filter((a) => a.activityId !== activityId));
    } catch (err) {
      appContext.setError(err);
    }
  };

  const personaStyle: Partial<IPersonaStyles> = {
    root: {
      marginBottom: 5,
      selectors: {
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const saveTask = async (task: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      await loadData(true);
    }
    setSelectedTask(undefined);
  };

  const removeTask = async (task: Task) => {
    setSelectedTask(undefined);
    await loadData(true);
  };

  const updateTask = async (task: Task) => {};

  return (
    <IPStack verticalFill styles={{ root: { paddingTop: 5 } }}>
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPList
            items={recentClosedDataDisplay}
            onRenderCell={(activity) => {
              if (!activity) return null;

              if (activity instanceof DateDivider) {
                return <DateDividerListItem {...activity} />;
              }

              if (activity instanceof DataGroup) {
                return null;
              }

              return (
                <IPStack
                  horizontal
                  onMouseOver={() => {
                    setShowIcon(activity.activityId);
                    if (recentClosedData) setRecentClosedData([...recentClosedData]);
                  }}
                  onMouseLeave={() => {
                    setShowIcon(undefined);
                    if (recentClosedData) setRecentClosedData([...recentClosedData]);
                  }}
                  styles={{ root: { paddingRight: 5 } }}
                >
                  <IPStack.Item styles={{ root: { width: '85%' } }}>
                    <IPPersona
                      styles={personaStyle}
                      size={IPPersonaSize.size40}
                      text={activity.entity.entityName || ''}
                      imageUrl={appContext.globalDataCache.users.getPhotoURLObj(activity.userId)}
                      secondaryText={toLocaleDateTimeMedium(activity.created)}
                      imageInitials={toNameInitial(appContext.globalDataCache.users.get(activity.userId).name)}
                      onClick={() => onPersonClickHandler(activity)}
                    />
                  </IPStack.Item>
                  {showIcon === activity.activityId && activity.dismissed === undefined && (
                    <IPStack.Item>
                      <IPTooltipHost content={t('dashboard:ActivityDismiss.label')}>
                        <IPIconButton iconProps={dismissIcon} onClick={() => dismissActivity(activity.activityId)} />
                      </IPTooltipHost>
                    </IPStack.Item>
                  )}
                </IPStack>
              );
            }}
          />
        </IPScrollablePane>
      </IPStack.Item>
      {recentClosedData?.length === 0 && (
        <Fragment>
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPStack.Item>
              <IPIconButton iconProps={SkypeCircleCheckIcon} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <IPText>{t('widgets:RecentlyClosedTasks.NoItems')}</IPText>
          </IPStack.Item>
        </Fragment>
      )}
      {selectedTask && (
        <SingleTask
          task={selectedTask}
          isOpen={true}
          close={() => {
            setSelectedTask(undefined);
          }}
          onSave={saveTask}
          onRemove={removeTask}
          onUpdate={updateTask}
        />
      )}
    </IPStack>
  );
};

export default WidgetRecentlyClosedTasks;
